/** Driver-only route estimates: no map, persisted route or automatic retry. */
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useShopSettings } from '@/context/ShopSettingsContext';
import { loadGoogleMapsLibraries } from '@/utils/googleMapsLoader';
import { isDesktopOffline } from '@/api/desktopSync';
import { googleRouteRequest, readGoogleRoute, ROUTE_WAIT_MS } from '@/utils/googleRoutes';
import { readOrderCoordinate } from '@/utils/mapCoordinates';
import { selectShopRouteOrigin } from '@/utils/shopRouteOrigin';

const useRunDistance = populatedStops => {
  const { routeOrigin } = useShopSettings();
  const origin = selectShopRouteOrigin(routeOrigin);
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const active = useRef(null);
  const requestedRetry = useRef(null);
  const offline = isDesktopOffline();
  const coordinates = (populatedStops || []).flatMap(stop => {
    const coordinate = readOrderCoordinate(stop.order);
    return coordinate ? [coordinate] : [];
  });
  const signature = JSON.stringify([origin.identity, coordinates]);
  const scope = useMemo(() => ({ signature, offline }), [signature, offline]);
  const latest = useRef(scope);
  latest.current = scope;

  useEffect(() => {
    const owner = { scope, active: true, phase: 'loading', timer: null };
    active.current = owner;
    const current = () => owner.active && active.current === owner && latest.current === scope && !isDesktopOffline();
    const pending = () => current() && owner.phase === 'loading';
    const publish = data => { if (current()) setResult({ scope, ...data }); };
    const fail = (error, kind) => {
      if (!pending()) return;
      clearTimeout(owner.timer);
      owner.phase = 'error'; owner.error = error; owner.kind = kind;
      publish({ routeInfo: null, loading: false, [kind]: error });
    };
    const retry = requestedRetry.current?.scope === scope && requestedRetry.current.kind === 'loadError';
    requestedRetry.current = null;
    if (!coordinates.length || offline || !origin.input) {
      owner.phase = 'idle';
      setResult({ scope, routeInfo: null, loading: false });
    } else {
      publish({ routeInfo: null, loading: true });
      // StrictMode's retired effect never starts a provider operation.
      Promise.resolve().then(async () => {
        if (!pending()) return;
        try {
          await loadGoogleMapsLibraries(['core', 'routes'], { retry });
        } catch (error) { fail(error, 'loadError'); return; }
        if (!pending()) return;
        owner.timer = setTimeout(() => fail({
          message: 'Route estimates took too long. Your run and orders have not changed.', retryable: true,
        }, 'routeError'), ROUTE_WAIT_MS);
        try {
          const response = await window.google.maps.routes.Route.computeRoutes(googleRouteRequest(origin.input, coordinates));
          if (!pending()) return;
          const { routeInfo } = readGoogleRoute(response, coordinates.length);
          clearTimeout(owner.timer); owner.phase = 'ready';
          publish({ routeInfo, loading: false });
        } catch {
          fail({ message: 'Route estimates are unavailable. Your run and orders have not changed.', retryable: true }, 'routeError');
        }
      });
    }
    return () => { owner.active = false; clearTimeout(owner.timer); };
    // Signature deliberately owns equivalent inputs; retries are single-use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, attempt]);

  // Existing name retained for callers; retries either loading or calculation.
  const retryLoading = useCallback(() => {
    const owner = active.current;
    if (isDesktopOffline() || latest.current !== scope || !owner?.active || owner.scope !== scope
      || owner.phase !== 'error' || !owner.error?.retryable) return;
    owner.phase = 'retrying';
    requestedRetry.current = { scope, kind: owner.kind };
    setAttempt(value => value + 1);
  }, [scope]);

  const visible = result?.scope === scope && !offline ? result : null;
  const needsOrigin = !offline && coordinates.length > 0;
  const originError = needsOrigin && origin.state === 'unavailable'
    ? { message: origin.error || 'Shop route origin is unavailable. Ask an administrator to update Shop Profile.', retryable: false }
    : needsOrigin && origin.stale
      ? { message: 'Using the last saved shop location; the latest settings check failed.', retryable: false }
      : null;
  return {
    routeInfo: visible?.routeInfo || null,
    loading: needsOrigin && origin.state === 'loading' ? true : (visible ? visible.loading : needsOrigin && origin.state === 'ready'),
    loadError: visible?.loadError || null, routeError: visible?.routeError || originError, retryLoading,
  };
};

export default useRunDistance;
